import { useState, useEffect } from "react";
import {
  Box,
  Stack,
  ListItem,
  TextField,
  Typography,
  Autocomplete,
  InputAdornment,
  CircularProgress
} from "@material-ui/core";
import matchSubstrings from "autosuggest-highlight/match";
import parseSubstrings from "autosuggest-highlight/parse";
import { useTranslation } from "react-i18next";
import { Search } from "@material-ui/icons";
import { LocationOn } from "@material-ui/icons";
import {
  AlgoliaResultType,
  getLocationAutocompleteSuggestions
} from "components/_dashboard/listingsMatch/algoliaClient";
import { useAlgoliaIndex } from "contexts/AlgoliaClientContext";
import { isPgSearchEnabled } from "utils/listingsSearchApi";
import useWebotServer from "hooks/useWebotServer";
import { getFullAddress } from "utils/listings/display";
import { PlaceAutoCompleteProps } from "./PlaceAutocomplete";
import type { SyntheticEvent } from "react";
import type { CreateAddressDetails } from "components/_dashboard/listingsMatch/CreateAddressDialog";
import type { ProcessedListingAddress } from "../../../../../@types/sharedSchema";

type AutoCompletePrediction = ProcessedListingAddress;

export function AlgoliaAutoComplete({
  type,
  disabled,
  required,
  inputValue,
  handleInputChange,
  handleAutoCompleteFill
}: PlaceAutoCompleteProps) {
  const [value, setValue] = useState<AutoCompletePrediction | null>(null);
  const [options, setOptions] = useState<AutoCompletePrediction[]>([]);
  const [loading, setLoading] = useState(false);

  const algoliaIndex = useAlgoliaIndex();
  const api = useWebotServer();

  const { t: translate } = useTranslation();

  const algoliaResultType = getAlgoliaResultType(type);

  useEffect(() => {
    if (!inputValue) return;
    // With server-side search there is no Algolia index; suggestions come from
    // the canonical location registry instead.
    if (!isPgSearchEnabled() && !algoliaIndex) return;

    setLoading(true);

    const fetchCanonicalSuggestions = async (): Promise<AutoCompletePrediction[]> => {
      // includeEmpty: this dialog defines an area to watch, so a place that
      // happens to hold no listing today is still a legitimate pick.
      const { suggestions } = await api.locationsAutocomplete(inputValue, {
        resultType: algoliaResultType,
        includeEmpty: true
      });

      // The registry populates only the fields that belong to a suggestion's
      // level (a street carries city + street, never a neighborhood). The
      // dropdown groups options by which fields are present, so an absent field
      // must stay absent rather than become an empty string.
      return suggestions.map((suggestion) => ({
        ...(suggestion.city ? { city: suggestion.city } : {}),
        ...(suggestion.neighborhood ? { neighborhood: suggestion.neighborhood } : {}),
        ...(suggestion.street ? { street: suggestion.street } : {})
      })) as AutoCompletePrediction[];
    };

    const fetchOptions = async (): Promise<void> => {
      try {
        const results = isPgSearchEnabled()
          ? await fetchCanonicalSuggestions()
          : await getLocationAutocompleteSuggestions({
              index: algoliaIndex,
              query: inputValue,
              resultType: algoliaResultType,
              removeNeighborhoodForStreetMatches: false
            });

        setOptions(results);
      } catch (error) {
        console.error("Error fetching location suggestions:", error);
      } finally {
        setLoading(false);
      }
    };

    const timeoutId = setTimeout(fetchOptions, 300);

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, type, inputValue, algoliaIndex, algoliaResultType]);

  useEffect(() => {
    if (!handleAutoCompleteFill) return;

    if (!value) return;

    const typesCanBeFilled = ["address", "neighborhood"].includes(type);

    if (!typesCanBeFilled) return;

    const fillAddressDetails = () => {
      const { neighborhood, city } = value;

      const canFillAddress = type === "address" && (neighborhood || city);

      if (canFillAddress) {
        const parsedAddressForm: Partial<CreateAddressDetails> = {};

        if (neighborhood) parsedAddressForm.neighborhood = neighborhood;
        if (city) parsedAddressForm.city = city;

        handleAutoCompleteFill(parsedAddressForm);
      }

      const canFillNeighborhood = type === "neighborhood" && city;

      if (canFillNeighborhood) {
        handleAutoCompleteFill({ city });
      }
    };

    fillAddressDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, algoliaResultType]);

  const handleChangeOptions = (
    _event: SyntheticEvent,
    newValue: AutoCompletePrediction | null
  ): void => {
    setValue(newValue);
  };

  const handleOptionsLabel = (option: AutoCompletePrediction): string => {
    if (typeof option === "string") return option;

    const parsedLabel = option[algoliaResultType];

    return parsedLabel;
  };

  return (
    <Autocomplete
      disabled={disabled}
      getOptionLabel={handleOptionsLabel}
      filterOptions={(x) => x}
      options={options}
      disableClearable
      includeInputInList
      filterSelectedOptions
      freeSolo
      value={inputValue}
      noOptionsText={translate("listingsPage.noLocations")}
      fullWidth
      onChange={handleChangeOptions}
      onInputChange={handleInputChange}
      renderInput={(params) => (
        <TextField
          {...params}
          label={type ? translate(`listingsPage.tableColumns.${type}`) : undefined}
          inputProps={{
            ...params.inputProps,
            autoComplete: "new-password"
          }}
          InputProps={{
            ...params.InputProps,
            ...(!type && {
              placeholder: translate("general.search"),
              startAdornment: (
                <InputAdornment sx={{ mx: 0.5 }} position="start">
                  <Search />
                </InputAdornment>
              )
            }),
            endAdornment: loading && <CircularProgress size={20} />
          }}
          required={required}
        />
      )}
      renderOption={(props, option) => {
        const mainText = option[algoliaResultType];

        const matches = matchSubstrings(mainText, inputValue);
        const textParts = parseSubstrings(mainText, matches);

        const fullAddress = getFullAddress(option);

        return (
          <ListItem {...props} key={fullAddress}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <LocationOn sx={{ color: "text.secondary" }} />
              <Box sx={{ wordWrap: "break-word" }}>
                {textParts.map(({ text, highlight }, index) => (
                  <Typography
                    variant="body1"
                    component="span"
                    color="text.primary"
                    fontWeight={highlight ? "bold" : "regular"}
                    key={index}
                  >
                    {text}
                  </Typography>
                ))}
                <Typography variant="body2" color="text.secondary">
                  {fullAddress}
                </Typography>
              </Box>
            </Stack>
          </ListItem>
        );
      }}
    />
  );
}

function getAlgoliaResultType(type: string): AlgoliaResultType {
  return type === "address" ? "street" : (type as AlgoliaResultType);
}

export type AlgoliaAutoCompleteIds = "address" | "neighborhood" | "city";
